import React, { useState, useRef, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Button, 
  Box,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Grid,
  IconButton,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import SmartEditor from '../smarteditor/component/sEditor';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import type { SmartEditorRef } from '../smarteditor/component/sEditor';
import { fetchApi } from '../services/api';
import Tooltip from '@mui/material/Tooltip';
import ConfirmDialog from './ConfirmDialog';

interface ScheduleData {
  업체번호: number;
  일정번호: number;
  상담번호: number;
  일정제목: string;
  일정내용: string;
  확정내용: string;
  안내사항: string;
  
}

interface ItineraryData {
  일정번호: number;
  일정명: string;
}

interface CounselingScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (content: string, url?: string) => void;
  initialContent?: number;
  initialItineraryNo?: number;
}

const CounselingScheduleDialog: React.FC<CounselingScheduleDialogProps> = ({
  open,
  onClose,
  onSave,
  initialContent,
  initialItineraryNo,
}) => {
  const editorRef = useRef<SmartEditorRef>(null);
  const [상담번호, set상담번호] = useState(initialContent);
  const [일정번호, set일정번호] = useState(initialItineraryNo);
  const [원본일정번호, set원본일정번호] = useState(initialItineraryNo);
  const [scheduleList, setScheduleList] = useState<ScheduleData[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<number | null>(null);
  const [currentSchedule, setCurrentSchedule] = useState<ScheduleData | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newScheduleTitle, setNewScheduleTitle] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSaveContent, setPendingSaveContent] = useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{업체번호: number; 일정번호: number} | null>(null);
  const [isItineraryDialogOpen, setIsItineraryDialogOpen] = useState(false);
  const [itineraryList, setItineraryList] = useState<ItineraryData[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [tempScheduleData, setTempScheduleData] = useState<string | null>(null);  // 임시 저장용 state 추가
  
  // const [원본일정번호 , set원본일정번호] = useState<number | null>(null);
  
  
  const fetchScheduleList = async () => {
    try {
      const response = await fetchApi<{ table: ScheduleData[] }>('ITN_S_간편상담일정조회', [
        { 컬럼명: '@상담번호', 값: 상담번호 }
      ]);

      if (response.data?.table && response.data.table.length > 0) {
        setScheduleList(response.data.table);
        setSelectedSchedule(response.data.table[0].일정번호);
        setCurrentSchedule(response.data.table[0]);
        if (editorRef.current) {
          editorRef.current.setContent(response.data.table[0].일정내용);
        }
      } else {
        // 일정이 없는 경우 기본 일정표 조회
        handleItinerary(일정번호 || 0);
      }
    } catch (error) {
      console.error('Failed to fetch schedule list:', error);
    }
  };
  
  const handleItinerary = async (itineraryNo: number) => {
    const itineraryResponse = await fetchApi<{ table: any[] }>('ITN_S_일정표', [
      { 컬럼명: '@일정번호', 값: itineraryNo }
    ]);

    if (itineraryResponse.data?.table && itineraryResponse.data.table.length > 0) {
      const itinerary = itineraryResponse.data.table[0];
      const newSchedule: ScheduleData = {
        업체번호: Number(localStorage.getItem('CompanyNo')) || 0,
        일정번호: 0,
        상담번호: 상담번호 || 0,
        일정제목: itinerary.일정명 || '',
        일정내용: itinerary.내용 || '',
        확정내용: '',
        안내사항: '',
      };
      setScheduleList([newSchedule]);
      setSelectedSchedule(0);
      setCurrentSchedule(newSchedule);
      if (editorRef.current) {
        editorRef.current.setContent(newSchedule.일정내용);
      }
    }
  };

  useEffect(() => {
    set상담번호(initialContent);
    set일정번호(initialItineraryNo);
    set원본일정번호(initialItineraryNo)
    if (open && initialContent) {
      fetchScheduleList();
    }
  }, [open, initialContent, initialItineraryNo]);

  const handleScheduleChange = (event: React.MouseEvent<HTMLElement>, newSchedule: number) => {
    if (newSchedule !== null) {
      setSelectedSchedule(newSchedule);
      const schedule = scheduleList.find(s => s.일정번호 === newSchedule);
      if (schedule) {
        setCurrentSchedule(schedule);
        if (editorRef.current) {
          editorRef.current.setContent(schedule.일정내용);
        }
      }
    }
  };

  const handleSave = async () => {
    if (!currentSchedule || !editorRef.current) return;
    
    const editorContent = editorRef.current.getContent();
    const scheduleData = {
      "일정번호": currentSchedule.일정번호,
      "상담번호": 상담번호,
      "일정제목": currentSchedule.일정제목,
      "일정내용": editorContent,
      "확정내용": currentSchedule.확정내용,
      "안내사항": currentSchedule.안내사항,
      "업체번호": Number(localStorage.getItem('CompanyNo')), 
      "원본일정번호": 원본일정번호,
      
    };

    if (!상담번호) {
      onSave(JSON.stringify(scheduleData), '');
      onClose();
      return;
    }

    // 기존 저장 로직...
    try {
      const 업체번호 = Number(localStorage.getItem('CompanyNo')) || 0;
      
      if (업체번호 === currentSchedule.업체번호) {
        const response = await fetchApi<{ table: ScheduleData[] }>('ITN_T_간편상담일정저장', [
          { 컬럼명: '@일정번호', 값: currentSchedule.일정번호 },
          { 컬럼명: '@상담번호', 값: 상담번호 },
          { 컬럼명: '@일정제목', 값: currentSchedule.일정제목 },
          { 컬럼명: '@일정내용', 값: editorContent },
          { 컬럼명: '@확정내용', 값: currentSchedule.확정내용 },
          { 컬럼명: '@안내사항', 값: currentSchedule.안내사항 },
          { 컬럼명: '@업체번호', 값: 업체번호 },
          // { 컬럼명: '@원본일정번호', 값: 원본일정번호 },
        ]);

        if (response.data?.table?.[0]) {
          // await fetchScheduleList();
          onSave(JSON.stringify(scheduleData), 원본일정번호? `https://landbox.kr/preview/${scheduleData.원본일정번호}?counselingId=${상담번호}` : '');
        }
      } else {
        setPendingSaveContent(editorContent);
        setConfirmDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to save schedule:', error);
    }
  };

  // const handleConfirmCopy = async () => {
  //   if (!currentSchedule) return;

  //   try {
  //     const response = await fetchApi<{ table: ScheduleData[] }>('ITN_T_간편상담일정저장', [
  //       { 컬럼명: '@일정번호', 값: 0 },
  //       { 컬럼명: '@상담번호', 값: 상담번호 },
  //       { 컬럼명: '@일정제목', 값: currentSchedule.일정제목 },
  //       { 컬럼명: '@일정내용', 값: pendingSaveContent },
  //       { 컬럼명: '@확정내용', 값: currentSchedule.확정내용 },
  //       { 컬럼명: '@안내사항', 값: currentSchedule.안내사항 },
  //       { 컬럼명: '@업체번호', 값: Number(localStorage.getItem('CompanyNo')) },
  //     ]);

  //     if (response.data?.table?.[0]) {
  //       const savedSchedule = response.data.table[0];
  //       const previewUrl = `https://landbox.kr/preview/${savedSchedule.일정번호}?counselingId=${상담번호}`;
  //       await fetchScheduleList();
  //       onSave(pendingSaveContent, previewUrl);
  //     }
  //   } catch (error) {
  //     console.error('Failed to copy schedule:', error);
  //   } finally {
  //     setConfirmDialogOpen(false);
  //     setPendingSaveContent('');
  //   }
  // };

  const handleAddSchedule = () => {
    if (!newScheduleTitle.trim()) return;

    const newSchedule: ScheduleData = {
      일정번호: 0, // 새로운 일정은 0으로 설정
      상담번호: 상담번호 || 0,
      일정제목: newScheduleTitle,
      일정내용: '',
      확정내용: '',
      안내사항: '',
    //   공유여부: false,
      업체번호: Number(localStorage.getItem('CompanyNo')) || 0,
    };

    setScheduleList([...scheduleList, newSchedule]);
    setSelectedSchedule(0);
    setCurrentSchedule(newSchedule);
    if (editorRef.current) {
      editorRef.current.setContent('');
    }

    setIsAddDialogOpen(false);
    setNewScheduleTitle('');
  };

  const handleDeleteClick = (e: React.MouseEvent, 업체번호: number, 일정번호: number) => {
    e.stopPropagation();
    setDeleteTarget({ 업체번호, 일정번호 });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      const response = await fetchApi<{ table: { Result: number }[] }>('ITN_T_간편상담일정삭제', [
        { 컬럼명: '@업체번호', 값: deleteTarget.업체번호 },
        { 컬럼명: '@일정번호', 값: deleteTarget.일정번호 }
      ]);

      await fetchScheduleList();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  // 내 일정 목록 조회
  const fetchItineraryList = async () => {
    try {
      const CompanyNo = localStorage.getItem('CompanyNo');
      const response = await fetchApi<{ table: ItineraryData[] }>('ITN_S_간편일정', [
        { 컬럼명: '@업체번호', 값: CompanyNo },
        { 컬럼명: '@즐겨찾기', 값: 2 },
      ]);

      if (response.data?.table) {
        setItineraryList(response.data.table);
      }
    } catch (error) {
      console.error('Failed to fetch itinerary list:', error);
    }
  };

  // 일정 선택 처리
  const handleItinerarySelect = (일정번호: number) => {
    set원본일정번호(일정번호);
    handleItinerary(일정번호);
    setIsItineraryDialogOpen(false);
  };

  // 필터링된 일정 목록 계산
  const filteredItineraryList = itineraryList.filter(itinerary => 
    itinerary.일정명.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 부모 컴포넌트로 임시 저장된 데이터를 전달하는 함수
  const getTempScheduleData = () => {
    return tempScheduleData;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: '1200px',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
          },
        }}>
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            mb: 2,
          }}>
          상담일정수정
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* {일정번호 === 0 && ( */}
              <Button
                variant="contained"
                startIcon={<FolderOpenIcon />}
                onClick={() => {
                  fetchItineraryList();
                  setIsItineraryDialogOpen(true);
                }}
                sx={{
                  width: '160px',
                  height: '36px',
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                }}>
                내일정 불러오기
              </Button>
            {/* )} */}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                width: '100px',
                height: '36px',
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
              }}>
              저장
            </Button>
            <Button
              variant="contained"
              startIcon={<CloseIcon />}
              onClick={onClose}
              sx={{
                width: '100px',
                height: '36px',
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
              }}>
              닫기
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography 
                sx={{ 
                  fontSize: '13px', 
                  color: 'text.secondary', 
                  mb: 0.5,
                  fontWeight: 500,
                }}>
                확정내용
              </Typography>
              <TextField
                multiline
                rows={5}
                fullWidth
                placeholder='고객일정표 상단에 확정내용으로 표시됩니다.'
                value={currentSchedule?.확정내용 || ''}
                onChange={(e) => {
                  if (currentSchedule) {
                    setCurrentSchedule({
                      ...currentSchedule,
                      확정내용: e.target.value
                    });
                  }
                }}
                inputProps={{
                  style: { padding: '8px' }  // 내부 여백 50% 감소
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    p: 0  // TextField 자체의 padding 제거
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography 
                sx={{ 
                  fontSize: '13px', 
                  color: 'text.secondary', 
                  mb: 0.5,
                  fontWeight: 500
                }}>
                안내사항
              </Typography>
              <TextField
                multiline
                rows={5}
                fullWidth
                placeholder='고객일정표 상단에 안내사항으로 표시됩니다.'
                value={currentSchedule?.안내사항 || ''}
                onChange={(e) => {
                  if (currentSchedule) {
                    setCurrentSchedule({
                      ...currentSchedule,
                      안내사항: e.target.value
                    });
                  }
                }}
                inputProps={{
                  style: { padding: '8px' }  // 내부 여백 50% 감소
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    p: 0  // TextField 자체의 padding 제거
                  }
                }}
              />
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={currentSchedule?.공유여부 || false}
                    onChange={(e) => {
                      if (currentSchedule) {
                        setCurrentSchedule({
                          ...currentSchedule,
                          공유여부: e.target.checked
                        });
                      }
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '13px' }}>
                    고객 공유여부
                  </Typography>
                }
                sx={{ mt: 1 }}
              /> */}
            </Grid>
          </Grid>
          <SmartEditor
            ref={editorRef}
            defaultValue={currentSchedule?.일정내용 || ''}
            height={window.innerHeight * 0.8 - 380}
          />
        </DialogContent>
      </Dialog>

      {/* 일정 추가 다이얼로그 */}
      <Dialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            mb: 2,

          }}>
          상담 일정(문서) 추가
        </DialogTitle>
        <DialogContent sx={{ pt: 5 }}>
          <TextField
            autoFocus
            fullWidth
            label="상담 일정(문서)명"
            value={newScheduleTitle}
            onChange={(e) => setNewScheduleTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={handleAddSchedule}
            disabled={!newScheduleTitle.trim()}
            sx={{
              width: '100px',
              height: '36px',
            }}>
            적용
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setIsAddDialogOpen(false);
              setNewScheduleTitle('');
            }}
            sx={{
              width: '100px',
              height: '36px',
            }}>
            취소
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* <ConfirmDialog
        open={confirmDialogOpen}
        message="타 업체가 작성한 일정표는 수정할 수 없습니다. 내 상담일정으로 복사할까요?"
        onConfirm={handleConfirmCopy}
        onClose={() => {
          setConfirmDialogOpen(false);
          setPendingSaveContent('');
        }}
      /> */}

      {/* 삭제 확인 다이얼로그 추가 */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        message="선택한 일정을 삭제하시겠습니까?"
        onConfirm={handleDeleteConfirm}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
      />

      {/* 내 일정 선택 다이얼로그 */}
      <Dialog
        open={isItineraryDialogOpen}
        onClose={() => setIsItineraryDialogOpen(false)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            // width: '960px',
            height: '80vh',
            maxWidth: 'none',
            maxHeight: 'none',
          },
        }}>
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
          }}>
          내일정선택
          <IconButton
            onClick={() => setIsItineraryDialogOpen(false)}
            sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="일정명으로 검색"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            sx={{
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                height: '36px',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          <List>
            {filteredItineraryList.map((itinerary) => (
              <ListItem
                key={itinerary.일정번호}
                divider
                sx={{
                  '&:hover': {
                    bgcolor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}>
                <ListItemText
                  primary={itinerary.일정명}
                  primaryTypographyProps={{
                    sx: { fontSize: '14px' }
                  }}
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleItinerarySelect(itinerary.일정번호)}
                    sx={{
                      minWidth: '60px',
                      height: '32px',
                    }}>
                    선택
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CounselingScheduleDialog;
